/**
 * A voucher's question answers and optional note, kept across a reload.
 *
 * They live in page state, so a reload used to lose them while `?step=` kept
 * the voucher on the review screen: the new look then held Submit back, and
 * the old look submitted the vouch with empty answers. Kept in
 * sessionStorage (this tab only, gone when it closes; never localStorage),
 * keyed by the voucher's session token, together with the question texts the
 * answers belong to, so a changed question list restores nothing. Cleared on
 * any terminal outcome. Every storage call is wrapped: private browsing can
 * throw, and losing a draft must never break the page.
 */

const KEY_PREFIX = "dv_voucher_answers_";

interface StoredDraft {
  questions: string[];
  answers: Record<number, string>;
  note: string;
}

export function saveVoucherAnswers(
  sessionToken: string,
  questions: readonly string[],
  answers: Readonly<Record<number, string>>,
  note: string,
): void {
  const draft: StoredDraft = { questions: [...questions], answers: { ...answers }, note };
  try {
    sessionStorage.setItem(KEY_PREFIX + sessionToken, JSON.stringify(draft));
  } catch {
    /* private mode or quota: the page works without the draft */
  }
}

export function loadVoucherAnswers(
  sessionToken: string,
  questions: readonly string[],
): { answers: Record<number, string>; note: string } | null {
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + sessionToken);
    if (!raw) return null;
    const draft = JSON.parse(raw) as Partial<StoredDraft>;
    if (
      !Array.isArray(draft.questions) ||
      draft.questions.length !== questions.length ||
      draft.questions.some((q, i) => q !== questions[i])
    ) {
      return null;
    }
    const answers: Record<number, string> = {};
    for (const [key, value] of Object.entries(draft.answers ?? {})) {
      const index = Number(key);
      if (Number.isInteger(index) && typeof value === "string") answers[index] = value;
    }
    return { answers, note: typeof draft.note === "string" ? draft.note : "" };
  } catch {
    return null;
  }
}

export function clearVoucherAnswers(sessionToken: string): void {
  try {
    sessionStorage.removeItem(KEY_PREFIX + sessionToken);
  } catch {
    /* nothing to clear if storage is unavailable */
  }
}
